import { useEffect, useMemo, useRef, useState } from "react";
import { YOY_BENEFICIARIES, YOY_SANCTIONED_CR, SANCTIONED_BREAKDOWN_RATIOS } from "../../data/ybf/timeseries";
import { PILLARS } from "../../data/ybf/pillars";
import { fmtInt } from "../../data/ybf/utils";

// Single shared scale across all cells — viewer can compare relative scale of work across pillars
const PALETTE = [
  "#F3F4F6", "#FEE2E2", "#FECACA", "#FCA5A5", "#F87171",
  "#EF4444", "#E2231A", "#B91C1C", "#7F1D1D",
];

function colorFor(value, max) {
  if (!value) return "#F8FAFC";
  const ratio = Math.min(value / max, 1);
  const idx = Math.min(Math.floor(ratio * PALETTE.length), PALETTE.length - 1);
  return PALETTE[idx];
}

function textOnFill(value, max) {
  if (!value) return "#94A3B8";
  return value / max > 0.45 ? "#FFFFFF" : "#0F172A";
}

const TABS = [
  { id: "beneficiaries", label: "Beneficiaries", data: YOY_BENEFICIARIES,
    fmtCell: (v) => fmtInt(v),
    fmtTotal: (v) => fmtInt(v),
    cellTitle: (p, fy, v) => `${p.name} · ${fy}: ${v.toLocaleString("en-IN")} beneficiaries`,
    maxLabel: (m) => `max ${fmtInt(m)}`,
    subtitle: "Darker = more lives impacted. Hover for exact counts.",
  },
  { id: "sanctioned",    label: "Sanctioned Amount", data: YOY_SANCTIONED_CR,
    fmtCell: (v) => v ? `₹${v.toFixed(1)}` : "—",
    fmtTotal: (v) => `₹${v.toFixed(1)} Cr`,
    cellTitle: (p, fy, v) => `${p.name} · ${fy}: ₹${v.toFixed(2)} Cr sanctioned`,
    maxLabel: (m) => `max ₹${m.toFixed(1)} Cr`,
    subtitle: "Darker = larger budget commitment (₹ Cr). Hover for exact figures.",
  },
];

export default function BeneficiariesHeatmap() {
  const [tab, setTab] = useState("beneficiaries");
  const [selected, setSelected] = useState(null); // { pillarId, fy } | null — only meaningful on sanctioned tab
  const active = TABS.find((t) => t.id === tab);
  const rows = active.data;
  const isSanctionedTab = tab === "sanctioned";

  // Dismiss popover when clicking outside the heatmap or when switching tabs
  const containerRef = useRef(null);
  useEffect(() => { setSelected(null); }, [tab]);
  useEffect(() => {
    function onDocClick(e) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setSelected(null);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const max = useMemo(
    () => Math.max(...rows.flatMap((r) => PILLARS.map((p) => r[p.id]))),
    [rows]
  );
  const totalsByPillar = useMemo(() => {
    const t = {};
    PILLARS.forEach((p) => {
      t[p.id] = rows.reduce((s, r) => s + (r[p.id] || 0), 0);
    });
    return t;
  }, [rows]);
  const totalsByYear = useMemo(
    () =>
      rows.map((r) => ({
        fy: r.fy,
        total: PILLARS.reduce((s, p) => s + (r[p.id] || 0), 0),
      })),
    [rows]
  );
  const grandTotal = rows.reduce(
    (s, r) => s + PILLARS.reduce((ss, p) => ss + r[p.id], 0),
    0
  );

  return (
    <div
      ref={containerRef}
      data-testid="beneficiaries-heatmap"
      className="bg-white rounded-2xl border border-slate-200 p-4 relative"
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-slate-900">
            {active.label} by Pillar — 4-Year Heatmap
          </h3>
          <p className="text-[11px] text-slate-500">
            {active.subtitle}
            {isSanctionedTab && (
              <span className="ml-1 text-[#1A1A1A] font-semibold">
                · Click any tile to view Disbursed &amp; Utilised
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Tabs */}
          <div
            className="inline-flex bg-slate-100 rounded-lg p-0.5"
            data-testid="heatmap-tabs"
          >
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`heatmap-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`text-[11px] font-semibold px-3 py-1.5 rounded-md transition-colors ${
                  tab === t.id
                    ? "bg-white text-[#1A1A1A] shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Cumulative</div>
            <div className="text-base font-bold text-[#1A1A1A]">
              {active.fmtTotal(grandTotal)}
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div
        className="grid gap-1.5"
        style={{
          gridTemplateColumns: `minmax(140px,1fr) repeat(${rows.length}, minmax(0,1fr)) minmax(80px,auto)`,
        }}
      >
        {/* Header row */}
        <div />
        {rows.map((r) => (
          <div
            key={r.fy}
            className="text-center text-[10px] font-bold uppercase tracking-wider text-slate-500 pb-1"
          >
            {r.fy}
          </div>
        ))}
        <div className="text-right text-[10px] font-bold uppercase tracking-wider text-slate-500 pb-1 pr-1">
          Total
        </div>

        {/* Pillar rows */}
        {PILLARS.map((p) => (
          <div key={p.id} className="contents">
            {/* Row label */}
            <div className="flex items-center gap-2 px-1">
              <span className="w-2 h-5 rounded-full flex-shrink-0" style={{ background: p.color }} />
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: p.color }}>
                  {p.short}
                </div>
                <div className="text-[11px] font-semibold text-slate-800 truncate">{p.name.split(" ")[0]}</div>
              </div>
            </div>

            {/* Cells */}
            {rows.map((r) => {
              const v = r[p.id] || 0;
              const isSelected = isSanctionedTab && selected && selected.pillarId === p.id && selected.fy === r.fy;
              const clickable = isSanctionedTab && v > 0;
              return (
                <div
                  key={r.fy + p.id}
                  data-testid={`heatmap-cell-${p.id}-${r.fy.replace(/\s+/g, "")}`}
                  onClick={
                    clickable
                      ? () => setSelected(isSelected ? null : { pillarId: p.id, fy: r.fy })
                      : undefined
                  }
                  className={`relative rounded-md flex items-center justify-center font-bold text-xs transition-all hover:scale-[1.03] ${
                    clickable ? "cursor-pointer" : "cursor-default"
                  } ${isSelected ? "ring-2 ring-offset-1 ring-[#E2231A] z-20" : ""}`}
                  style={{
                    background: colorFor(v, max),
                    color: textOnFill(v, max),
                    aspectRatio: "3.2 / 1",
                    minHeight: 22,
                  }}
                  title={
                    clickable
                      ? `${active.cellTitle(p, r.fy, v)}\nClick to see Disbursed & Utilised`
                      : active.cellTitle(p, r.fy, v)
                  }
                >
                  {active.fmtCell(v)}

                  {isSelected && (
                    <BreakdownPopover
                      pillar={p}
                      fy={r.fy}
                      sanctioned={v}
                      onClose={() => setSelected(null)}
                    />
                  )}
                </div>
              );
            })}

            {/* Row total */}
            <div className="flex items-center justify-end pr-1">
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-md"
                style={{ background: `${p.color}10`, color: p.color }}
              >
                {active.fmtTotal(totalsByPillar[p.id])}
              </span>
            </div>
          </div>
        ))}

        {/* Footer totals row */}
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold pt-2 pl-1">
          Year total
        </div>
        {totalsByYear.map((y) => (
          <div
            key={"total-" + y.fy}
            className="text-center text-xs font-bold text-slate-800 pt-2"
          >
            {active.fmtTotal(y.total)}
          </div>
        ))}
        <div />
      </div>

      {/* Legend */}
      <div className="flex items-center gap-2 mt-4 px-1">
        <span className="text-[10px] text-slate-500 font-medium">Fewer</span>
        <div className="flex flex-1 h-2 rounded-full overflow-hidden">
          {PALETTE.map((c) => (
            <div key={c} className="flex-1 h-full" style={{ background: c }} />
          ))}
        </div>
        <span className="text-[10px] text-slate-500 font-medium">More</span>
        <span className="text-[10px] text-slate-400 ml-2">({active.maxLabel(max)})</span>
      </div>
    </div>
  );
}

function BreakdownPopover({ pillar, fy, sanctioned, onClose }) {
  const ratios = SANCTIONED_BREAKDOWN_RATIOS[fy] || { disb: 0.85, util: 0.7 };
  const disbursed = +(sanctioned * ratios.disb).toFixed(2);
  const utilised  = +(sanctioned * ratios.util).toFixed(2);
  const disbPct = Math.round(ratios.disb * 100);
  const utilPct = Math.round(ratios.util * 100);
  const utilOfDisb = Math.round((utilised / disbursed) * 100);

  return (
    <div
      data-testid={`breakdown-popover-${pillar.id}-${fy.replace(/\s+/g, "")}`}
      onClick={(e) => e.stopPropagation()}
      className="absolute z-30 left-1/2 -translate-x-1/2 bottom-full mb-2 w-[280px] bg-white rounded-xl shadow-xl border border-slate-200 p-3 text-slate-900"
      style={{ filter: "drop-shadow(0 8px 20px rgba(0,32,78,0.18))" }}
    >
      {/* Arrow */}
      <span
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-3 h-3 rotate-45 bg-white border-r border-b border-slate-200"
      />

      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-wider font-bold" style={{ color: pillar.color }}>
            {pillar.short} · {fy}
          </div>
          <div className="text-[12px] font-bold text-slate-900 truncate">
            Funding breakdown
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="breakdown-popover-close"
          className="text-slate-400 hover:text-slate-700 text-xs leading-none px-1.5 py-0.5 rounded hover:bg-slate-100"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Bars */}
      <BreakdownRow
        label="Sanctioned"
        value={`₹${sanctioned.toFixed(2)} Cr`}
        pct={100}
        color={pillar.color}
        showPct={false}
      />
      <BreakdownRow
        label="Disbursed"
        value={`₹${disbursed.toFixed(2)} Cr`}
        pct={disbPct}
        color="#0EA5E9"
        chip={`${disbPct}% of sanctioned`}
      />
      <BreakdownRow
        label="Utilised"
        value={`₹${utilised.toFixed(2)} Cr`}
        pct={utilPct}
        color="#16A34A"
        chip={`${utilPct}% of sanctioned · ${utilOfDisb}% of disbursed`}
      />
    </div>
  );
}

function BreakdownRow({ label, value, pct, color, chip, showPct = true }) {
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex items-baseline justify-between text-[10px] mb-0.5">
        <span className="font-bold uppercase tracking-wider text-slate-500">{label}</span>
        <span className="text-[12px] font-bold" style={{ color }}>{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      {showPct && chip && (
        <div className="text-[9px] text-slate-500 mt-0.5">{chip}</div>
      )}
    </div>
  );
}

